import { SubscriptionStatus } from '@app/config';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';

export interface Subscription {
    title: string;
    subtitle: string;
    constraintText: string;
    documentConstraintText?: string;
    stripeId: string;
    active: boolean;
    status: SubscriptionStatus;
    price: number;
    monthlyPrice: number;
    currency: string;
    billingPeriod: string;
    taxDescription: string;
    created: Date;
    currentPeriodStart: Date;
    currentPeriodEnd: Date;
    renewDate: Date;
    remainingDays: number;
    nextInvoiceCost: number | string;
    nextInvoiceTaxDescription: string;
    dueDate: Date;
    isEocFeatureEnabled?: boolean;
    counts: {
        agreements: number;
        documents: number;
        eoc: number;
    };
    limits: {
        agreements: number;
        documents: number;
    };
    agreementsPerMonth: {
        min: number;
        max: number;
    };
    hasFreeTrial?: boolean;
    metadata?: {
        blockDashboardPlanChange?: boolean;
    };
}

interface SubscriptionResponse {
    subscription: Subscription;
}

/**
 * Returns subscription for agency Id param
 * @param agencyId
 * @returns {Promise<AxiosResponse<any>>}
 */
const fetchSubscriptionRequest = (agencyId: string): Promise<SubscriptionResponse> => {
    return axios.get(`/api/agency/${agencyId}/subscription`).then(res => res.data);
};

/**
 * Hook for retrieving subscription for agency
 * @param agencyId
 * @param onSuccess
 * @param onError
 * @returns {UseQueryResult<unknown, unknown>}
 */
export const useGetSubscription = (
    agencyId: string,
    onSuccess?: (data: Subscription) => void,
    onError?: (error: unknown) => void
): UseQueryResult<Subscription, unknown> => {
    return useQuery<SubscriptionResponse, unknown, Subscription>(
        ['subscription', agencyId],
        () => fetchSubscriptionRequest(agencyId),
        {
            onSuccess,
            onError,
            enabled: agencyId !== undefined,
            staleTime: 2000,
            refetchInterval: 5 * 60 * 1000,
            refetchIntervalInBackground: true,
            select: data => data.subscription
        }
    );
};

const cancelSubscriptionRequest = (agencyId: string) => {
    return axios.delete(`/api/agency/${agencyId}/tariff-plan`);
};

/**
 * Cancel subscription from the agency
 * @param agencyId
 * @param onSuccess
 * @param onError
 * @returns {UseMutationResult<AxiosResponse<*>, unknown, void, unknown>}
 */
export const useCancelSubscription = (agencyId: string, onSuccess?: () => void, onError?: () => void) => {
    const queryClient = useQueryClient();
    return useMutation(() => cancelSubscriptionRequest(agencyId), {
        onSuccess: async () => {
            await queryClient.invalidateQueries(['subscription', agencyId]);
            onSuccess?.();
        },
        onError: () => {
            onError?.();
        }
    });
};

const setSubscriptionPlanRequest = (agencyId: string, stripeId: string) => {
    return axios.post(`/api/agency/${agencyId}/tariff-plan`, { stripeId: stripeId }).then(res => res.data.tariffPlan);
};

type SetSubscriptionPlanVariables = {
    stripeId: string;
};

/**
 * Mutation hook to set subscription Plan
 * @param agencyId
 * @param onSuccess
 * @param onError
 * @returns {UseMutationResult<unknown, unknown, SetSubscriptionPlanVariables, unknown>}
 */
export const useSetSubscriptionPlan = (
    agencyId: string,
    onSuccess?: () => void,
    onError?: () => void
): UseMutationResult<unknown, unknown, SetSubscriptionPlanVariables, unknown> => {
    const queryClient = useQueryClient();
    return useMutation(({ stripeId }) => setSubscriptionPlanRequest(agencyId, stripeId), {
        onSuccess: async () => {
            await queryClient.invalidateQueries(['subscription', agencyId]);
            onSuccess?.();
        },
        onError: () => {
            onError?.();
        }
    });
};

const resumeSubscriptionRequest = (agencyId: string) => {
    return axios.post(`/api/agency/${agencyId}/resume-subscription`).then(res => res.data);
};

/**
 * Mutation hook to resume subscription Plan
 * @param agencyId
 * @param onSuccess
 * @param onError
 * @returns {UseMutationResult<unknown, unknown, void, unknown>}
 */
export const useResumeSubscription = (
    agencyId: string,
    onSuccess?: () => void,
    onError?: () => void
): UseMutationResult<unknown, unknown, void, unknown> => {
    const queryClient = useQueryClient();
    return useMutation(() => resumeSubscriptionRequest(agencyId), {
        onSuccess: async () => {
            // Wait 7.5 seconds and then fetch the tariffPlan again that should now have been updated
            setTimeout(async () => {
                await queryClient.invalidateQueries(['subscription', agencyId]);
                onSuccess?.();
            }, 7500);
        },
        onError: () => {
            onError?.();
        }
    });
};
